import { decodePayload } from './codec.js';
import { computeHmac } from './generate.js';

export interface ValidationOk {
  ok: true;
  qrIdHex: string;
}

export interface ValidationFail {
  ok: false;
  reason: 'tampered' | 'malformed';
}

export type ValidationResult = ValidationOk | ValidationFail;

/**
 * Verifica un payload contra el secret correspondiente. Esta función es
 * pure: no consulta red, ni base de datos. La orquestación (buscar el
 * secret por qrId, marcar como validado, etc.) corre por fuera.
 *
 * Debe usarse igualmente en el servidor (validación online) y en el
 * scanner offline (validación local con bundle pre-sincronizado).
 */
export async function validatePayload(
  payload: string,
  resolveSecret: (qrIdHex: string) => Promise<Uint8Array | null> | Uint8Array | null,
): Promise<ValidationResult> {
  let decoded;
  try {
    decoded = decodePayload(payload);
  } catch {
    return { ok: false, reason: 'malformed' };
  }

  const secret = await resolveSecret(decoded.qrIdHex);
  if (!secret) {
    return { ok: false, reason: 'tampered' };
  }

  const expected = await computeHmac(secret, decoded.qrIdBytes);
  if (!constantTimeEqual(expected, decoded.hmacBytes)) {
    return { ok: false, reason: 'tampered' };
  }

  return { ok: true, qrIdHex: decoded.qrIdHex };
}

function constantTimeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= (a[i] ?? 0) ^ (b[i] ?? 0);
  return diff === 0;
}
